import React, { FC, useState, useEffect } from 'react'
import Container from 'react-bootstrap/Container'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import FhirLoader from '../../components/FhirLoader'
import Alert from 'react-bootstrap/Alert'
import { useFormik } from 'formik'
import { ContactSchema } from '../../utils/validation'
import useSendQuestion from '../../hooks/help/useSendQuestion'
import PageTitle from '../../components/PageTitle'
import './index.scss'

const Contact: FC = () => {
  const [alertState, setAlertState] = useState({ show: false, variant: 'info', message: '' })
  const sendQuestion = useSendQuestion()

  useEffect(() => {
    if (sendQuestion.isSuccess) {
      setAlertState({
        show: true,
        variant: 'success',
        message: 'Thank you for contacting HealthLX. We will get back to as soon as we can, but usually within one business day.'
      })
    }
    if (sendQuestion.isError) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Unable to send message: '
      })
    }
  }, [sendQuestion.isSuccess, sendQuestion.isError])

  const contactForm = useFormik({
    initialValues: {
      name: '',
      email: '',
      memberId: '',
      message: ''
    },
    onSubmit: (values) => {
      sendQuestion.mutate(values)
    },
    validationSchema: ContactSchema,
    enableReinitialize: true
  })

  return (
    <section>
      <Container
        className='contact-container'
      >
        <PageTitle displayName="Contact HealthLX" />
        <h1 className='h2'>Contact HealthLX</h1>
        <p>
          Please use this form to send an email to HealthLX. We will get back to you as soon as we can, but usually within one business day.
        </p>
        <Alert show={alertState.show} variant={alertState.variant} dismissible onClose={() => setAlertState((ps) => ({ ...ps, show: false }))}>
          {alertState.message}
          {
            sendQuestion.isError && (<div dangerouslySetInnerHTML={{ __html: sendQuestion.error?.response.text }} />)
          }
        </Alert>
        <Form noValidate onSubmit={contactForm.handleSubmit}>
          <Form.Group>
            <Form.Label>Name:</Form.Label>
            <Form.Control
              id="name"
              name="name"
              type="text"
              value={contactForm.values.name}
              onChange={contactForm.handleChange}
              onBlur={contactForm.handleBlur}
              isInvalid={!!contactForm.errors.name && contactForm.touched.name}
              aria-invalid={!!contactForm.errors.name && contactForm.touched.name}
              aria-describedby={contactForm.errors.name && contactForm.touched.name ? 'name-error' : ''}
              aria-required="true"
            />
            <Form.Control.Feedback id="name-error" type="invalid" role="alert">
              {contactForm.errors.name}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Email:</Form.Label>
            <Form.Control
              id="email"
              name="email"
              type="text"
              value={contactForm.values.email}
              onChange={contactForm.handleChange}
              onBlur={contactForm.handleBlur}
              isInvalid={!!contactForm.errors.email && contactForm.touched.email}
              aria-invalid={!!contactForm.errors.email && contactForm.touched.email}
              aria-describedby={contactForm.errors.email && contactForm.touched.email ? 'email-error' : ''}
              aria-required="true"
            />
            <Form.Control.Feedback id="email-error" type="invalid" role="alert">
              {contactForm.errors.email}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Phone:</Form.Label>
            <Form.Control
              id="memberId"
              name="memberId"
              type="text"
              value={contactForm.values.memberId}
              onChange={contactForm.handleChange}
              onBlur={contactForm.handleBlur}
              isInvalid={!!contactForm.errors.memberId && contactForm.touched.memberId}
              aria-invalid={!!contactForm.errors.memberId && contactForm.touched.memberId}
              aria-describedby={contactForm.errors.memberId && contactForm.touched.memberId ? 'memberId-error' : ''}
              aria-required="true"
            />
            <Form.Control.Feedback id="memberId-error" type="invalid" role="alert">
              {contactForm.errors.memberId}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>How Can We Help?:</Form.Label>
            <Form.Control
              id="message"
              name="message"
              as="textarea"
              rows={6}
              value={contactForm.values.message}
              onChange={contactForm.handleChange}
              onBlur={contactForm.handleBlur}
              isInvalid={!!contactForm.errors.message && contactForm.touched.message}
              aria-invalid={!!contactForm.errors.message && contactForm.touched.message}
              aria-describedby={contactForm.errors.message && contactForm.touched.message ? 'message-error' : ''}
              aria-required="true"
            />
           <Form.Control.Feedback id="message-error" type="invalid" role="alert">
              {contactForm.errors.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Button type='submit' variant='primary' disabled={sendQuestion.isLoading}>
            {
              sendQuestion.isLoading ? (
                <FhirLoader small={true} />
              ) : 'Send'
            }
          </Button>
        </Form>
      </Container>
    </section>
  )
}

export default Contact